/**
 * Fase 8 — tela "Assistente de IA": histórico de conversas (análises e diagnósticos), briefing →
 * estrutura de campanha → "Criar rascunho com esta estrutura", regras sugeridas a partir das metas do
 * cliente (criadas desativadas) e uso do mês por recurso com a cota de créditos.
 */
import {
  AI_RESOURCE_LABELS,
  CAMPAIGN_OBJECTIVES,
  objectiveLabel,
  RULE_LEVEL_LABELS,
  roleHas,
  type AiConversationDto,
  type AiRuleSuggestionsDto,
  type AiStructureDto,
  type CampaignObjective,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { MessageSquare, Plus, Shield, Sparkles, Trash2, Wand2 } from "lucide-react";
import { useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { AiPanel } from "@/components/ai/ai-panel";
import { Markdown } from "@/components/ai/markdown";
import { CreditsBadge } from "@/components/ai/credits-badge";
import { PERIOD_ALL, PeriodPicker, type PeriodValue } from "@/components/campaigns/period-picker";
import { ClientFilter } from "@/components/client-filter";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage } from "@/lib/api";
import { aiApi, aiKey, aiQueries, formatUsdMicros } from "@/lib/ai-api";
import { creationKey } from "@/lib/creation-api";
import { queries } from "@/lib/queries";
import { rulesKey } from "@/lib/fase7-api";
import { formatDate, formatMoney, formatRelative } from "@/lib/utils";
import { periodToParams } from "./relatorios";

interface IaSearch {
  aba?: "conversas" | "estrutura" | "regras" | "uso";
  conversa?: string;
}

export const Route = createFileRoute("/app/$workspaceId/ia")({
  validateSearch: (raw: Record<string, unknown>): IaSearch => {
    const out: IaSearch = {};
    if (raw.aba === "conversas" || raw.aba === "estrutura" || raw.aba === "regras" || raw.aba === "uso") out.aba = raw.aba;
    if (typeof raw.conversa === "string" && raw.conversa) out.conversa = raw.conversa;
    return out;
  },
  component: IaPage,
});

function IaPage() {
  const { workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const workspace = useQuery(queries.workspace(workspaceId));
  const status = useQuery(aiQueries.status(workspaceId));
  const role = workspace.data?.role;
  const canWrite = role ? roleHas(role, "ai:write") : false;
  const tab = search.aba ?? "conversas";
  const setSearch = (p: { aba?: IaSearch["aba"]; conversa?: string | undefined }) =>
    void navigate({
      search: (prev) => {
        const next: IaSearch = {};
        const aba = "aba" in p ? p.aba : prev.aba;
        const conversa = "conversa" in p ? p.conversa : prev.conversa;
        if (aba) next.aba = aba;
        if (conversa) next.conversa = conversa;
        return next;
      },
    });

  if (workspace.isPending) return <LoadingState rows={4} />;
  if (workspace.isError) return <ErrorState error={workspace.error} onRetry={() => void workspace.refetch()} />;

  return (
    <>
      <PageHeader
        title="Assistente de IA"
        description="Analisa contas e campanhas, escreve variações de anúncio, sugere estrutura, públicos e regras. A IA sugere; você aprova — nada é publicado ou alterado na Meta por ela."
        actions={<CreditsBadge workspaceId={workspaceId} className="h-8 text-sm" />}
      />
      {status.data && !status.data.configured ? (
        <Alert className="mb-4">
          <AlertTitle>IA em modo real sem chave</AlertTitle>
          <AlertDescription>Defina o secret ANTHROPIC_API_KEY no servidor (ou AI_MODE=mock em dev). Até lá, as chamadas vão falhar com esta mensagem.</AlertDescription>
        </Alert>
      ) : null}
      <div className="space-y-4">
        <Tabs value={tab} onValueChange={(v) => setSearch({ aba: v === "estrutura" || v === "regras" || v === "uso" ? v : "conversas", conversa: undefined })}>
          <TabsList>
            <TabsTrigger value="conversas">Conversas</TabsTrigger>
            {canWrite ? <TabsTrigger value="estrutura">Estrutura e públicos</TabsTrigger> : null}
            {canWrite ? <TabsTrigger value="regras">Regras sugeridas</TabsTrigger> : null}
            <TabsTrigger value="uso">Uso do mês</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "conversas" ? (
          <ConversationsPanel workspaceId={workspaceId} selected={search.conversa} onSelect={(id) => setSearch({ conversa: id })} />
        ) : tab === "estrutura" && canWrite ? (
          <StructurePanel workspaceId={workspaceId} />
        ) : tab === "regras" && canWrite ? (
          <RulesPanel workspaceId={workspaceId} />
        ) : (
          <UsagePanel workspaceId={workspaceId} />
        )}
      </div>
    </>
  );
}

// ---- Conversas ---------------------------------------------------------------------------------------

const DEFAULT_PERIOD: PeriodValue = { preset: "30d", range: { from: null, to: null } };

function ConversationsPanel({ workspaceId, selected, onSelect }: { workspaceId: string; selected: string | undefined; onSelect: (id: string | undefined) => void }) {
  const queryClient = useQueryClient();
  const list = useQuery(aiQueries.conversations(workspaceId));
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [period, setPeriod] = useState<PeriodValue>(DEFAULT_PERIOD);
  const [fresh, setFresh] = useState(0);
  const [toDelete, setToDelete] = useState<AiConversationDto | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => aiApi.deleteConversation(workspaceId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: aiKey(workspaceId) });
      if (toDelete?.id === selected) onSelect(undefined);
      setToDelete(null);
      toast.success("Conversa excluída.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const context = { ...periodToParams(period), ...(clientId ? { clientId } : {}) };
  const current = list.data?.find((c) => c.id === selected);

  return (
    <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
      <div className="space-y-3">
        <div className="space-y-2 rounded-xl border p-3">
          <p className="text-sm font-medium">Nova conversa</p>
          <ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} className="w-full" />
          <PeriodPicker value={period} onChange={(v) => setPeriod(v.preset === "maximo" ? PERIOD_ALL : v)} />
          <Button
            className="w-full"
            onClick={() => {
              onSelect(undefined);
              setFresh((n) => n + 1);
            }}
          >
            <Plus /> Começar
          </Button>
        </div>
        {list.isPending ? (
          <LoadingState rows={3} />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.data.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">Nenhuma conversa ainda. As análises e os diagnósticos ficam aqui.</p>
        ) : (
          <ul className="max-h-[60vh] space-y-1 overflow-y-auto" aria-label="Histórico de conversas">
            {list.data.map((c) => (
              <li key={c.id} className="group flex items-start gap-1">
                <button
                  type="button"
                  onClick={() => onSelect(c.id)}
                  className={`min-w-0 flex-1 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent ${c.id === selected ? "bg-accent font-medium" : ""}`}
                >
                  <span className="block truncate">{c.title || "Sem título"}</span>
                  <span className="block text-[11px] text-muted-foreground">
                    {c.kind === "diagnostico" ? "Diagnóstico" : "Análise"} · {c.messageCount} msg · {formatRelative(c.lastMessageAt ?? c.createdAt)}
                  </span>
                </button>
                <Button variant="ghost" size="icon" className="size-7 opacity-0 group-hover:opacity-100" aria-label="Excluir conversa" onClick={() => setToDelete(c)}>
                  <Trash2 className="size-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        {current?.kind === "diagnostico" ? (
          <DiagnosisView workspaceId={workspaceId} conversationId={current.id} />
        ) : (
          <AiPanel
            key={`${selected ?? "nova"}-${fresh}`}
            workspaceId={workspaceId}
            variant="inline"
            context={context}
            conversationId={selected ?? null}
            title={current ? current.title || "Conversa" : "Nova conversa"}
            subtitle={current ? "Continue a conversa; o contexto é remontado com dados atualizados a cada pergunta." : "Escolha o cliente e o período acima; o contexto (KPIs, campanhas, criativos, quebras, metas e alertas) vai junto com a pergunta."}
          />
        )}
      </div>
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Excluir conversa?"
        description="O histórico desta conversa será apagado. Os créditos já usados não voltam."
        confirmLabel="Excluir"
        destructive
        loading={remove.isPending}
        onConfirm={() => toDelete && remove.mutate(toDelete.id)}
      />
    </div>
  );
}

function DiagnosisView({ workspaceId, conversationId }: { workspaceId: string; conversationId: string }) {
  const detail = useQuery(aiQueries.conversation(workspaceId, conversationId));
  if (detail.isPending) return <LoadingState rows={4} />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  const answer = detail.data.messages.find((m) => m.role === "assistant");
  return (
    <Card>
      <CardHeader>
        <CardTitle>{detail.data.title}</CardTitle>
        <CardDescription>
          Diagnóstico de {formatDate(detail.data.createdAt)} ·{" "}
          <Link
            to="/app/$workspaceId/campanhas"
            params={{ workspaceId }}
            search={{
              ...(detail.data.context.metaId ? { item: detail.data.context.metaId } : {}),
              ...(detail.data.context.level === "campaigns" || detail.data.context.level === "adsets" || detail.data.context.level === "ads"
                ? { nivel: detail.data.context.level }
                : {}),
            }}
            className="underline"
          >
            abrir o item na tabela
          </Link>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {answer ? <Markdown text={answer.content} /> : <p className="text-sm text-muted-foreground">Sem resposta registrada.</p>}
      </CardContent>
    </Card>
  );
}

// ---- Estrutura e públicos -------------------------------------------------------------------------------

function StructurePanel({ workspaceId }: { workspaceId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accounts = useQuery(queries.campaignAccounts(workspaceId));
  const [account, setAccount] = useState("");
  const [business, setBusiness] = useState("");
  const [offer, setOffer] = useState("");
  const [region, setRegion] = useState("");
  const [budget, setBudget] = useState("80");
  const [objective, setObjective] = useState<CampaignObjective>("OUTCOME_LEADS");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState<AiStructureDto | null>(null);
  const act = account !== "" ? account : (accounts.data?.[0]?.metaId ?? "");
  const currency = accounts.data?.find((a) => a.metaId === act)?.currency ?? "BRL";
  const suggest = useMutation({
    mutationFn: () =>
      aiApi.structure(workspaceId, {
        adAccountMetaId: act,
        business,
        offer,
        region,
        dailyBudgetCents: Math.round(Number(budget.replace(",", ".")) * 100),
        objective,
        notes,
      }),
    onSuccess: (r) => {
      setResult(r);
      void queryClient.invalidateQueries({ queryKey: aiKey(workspaceId) });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const createDraft = useMutation({
    mutationFn: () => aiApi.structureDraft(workspaceId, { adAccountMetaId: act, structure: result?.structure ?? ({} as AiStructureDto["structure"]) }),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      if (r.unresolved.length > 0) {
        toast.warning(`Rascunho criado. Não encontrei na Meta: ${r.unresolved.map((u) => u.term).join(", ")}. Complete no assistente.`);
      } else {
        toast.success("Rascunho criado. Revise cada etapa antes de publicar.");
      }
      void navigate({ to: "/app/$workspaceId/assistente/$draftId", params: { workspaceId, draftId: r.draftId } });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const submit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!act) {
      toast.error("Escolha a conta de anúncio.");
      return;
    }
    suggest.mutate();
  };
  const s = result?.structure;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="size-4" aria-hidden /> Briefing
          </CardTitle>
          <CardDescription>Negócio, oferta, região, orçamento e objetivo → campanha, conjuntos com segmentação, quantidade de anúncios e públicos sugeridos.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
            <FormField id="b-conta" label="Conta de anúncio">
              <Select value={act} onValueChange={setAccount}>
                <SelectTrigger id="b-conta">
                  <SelectValue placeholder="Escolha a conta" />
                </SelectTrigger>
                <SelectContent>
                  {(accounts.data ?? []).map((a) => (
                    <SelectItem key={a.metaId} value={a.metaId}>
                      {a.name} · {a.currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField id="b-objetivo" label="Objetivo">
              <Select value={objective} onValueChange={(v) => setObjective(v as CampaignObjective)}>
                <SelectTrigger id="b-objetivo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_OBJECTIVES.map((o) => (
                    <SelectItem key={o} value={o}>
                      {objectiveLabel(o)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField id="b-negocio" label="Negócio">
              <Input id="b-negocio" value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="Ex.: clínica de estética em Campinas" required minLength={3} maxLength={500} />
            </FormField>
            <FormField id="b-oferta" label="Oferta (opcional)">
              <Input id="b-oferta" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Ex.: avaliação grátis" maxLength={500} />
            </FormField>
            <FormField id="b-regiao" label="Região">
              <Input id="b-regiao" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Ex.: Campinas, SP" required minLength={2} maxLength={200} />
            </FormField>
            <FormField id="b-orcamento" label={`Orçamento diário total (${currency})`}>
              <Input id="b-orcamento" value={budget} onChange={(e) => setBudget(e.target.value)} inputMode="decimal" required />
            </FormField>
            <div className="sm:col-span-2">
              <FormField id="b-notas" label="Observações (opcional)">
                <Textarea id="b-notas" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000} />
              </FormField>
            </div>
            <div className="flex justify-end sm:col-span-2">
              <Button type="submit" loading={suggest.isPending}>
                <Sparkles /> Sugerir estrutura
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {s ? (
        <Card>
          <CardHeader>
            <CardTitle>{s.campaignName}</CardTitle>
            <CardDescription>
              {objectiveLabel(s.objective)} · {s.budgetMode === "CBO" ? "orçamento na campanha (CBO)" : "orçamento por conjunto (ABO)"} · {s.adsets.length} conjunto(s) · {result?.credits} crédito(s)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Conjunto</TableHead>
                  <TableHead>Segmentação</TableHead>
                  <TableHead className="text-right">Orçamento/dia</TableHead>
                  <TableHead className="text-right">Anúncios</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {s.adsets.map((a) => (
                  <TableRow key={a.name}>
                    <TableCell>
                      <p className="font-medium">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.rationale}</p>
                    </TableCell>
                    <TableCell className="text-xs">
                      {a.locations.join(", ")} · {a.ageMin}–{a.ageMax} anos · {a.genders}
                      {a.interests.length > 0 ? ` · interesses: ${a.interests.join(", ")}` : ""}
                      {a.advantageAudience ? " · Advantage+" : ""}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(a.dailyBudgetCents, currency)}</TableCell>
                    <TableCell className="text-right">{a.adCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {s.audiences.length > 0 ? (
              <div>
                <p className="text-sm font-medium">Públicos a criar</p>
                <ul className="mt-1 space-y-1 text-sm">
                  {s.audiences.map((a) => (
                    <li key={a.name}>
                      <Badge variant="outline" className="mr-2">
                        {a.kind}
                      </Badge>
                      <strong>{a.name}</strong> — {a.description}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {s.notes.length > 0 ? (
              <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {s.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            ) : null}
            <div className="flex justify-end">
              <Button onClick={() => createDraft.mutate()} loading={createDraft.isPending}>
                <Wand2 /> Criar rascunho com esta estrutura
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">O rascunho abre no assistente de criação para você revisar público, mídia e textos. Nada é publicado pela IA.</p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ---- Regras sugeridas -------------------------------------------------------------------------------------

function RulesPanel({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const clients = useQuery(queries.clients(workspaceId));
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<AiRuleSuggestionsDto | null>(null);
  const [created, setCreated] = useState<Record<string, string>>({});
  const suggest = useMutation({
    mutationFn: (id: string) => aiApi.suggestRules(workspaceId, id),
    onSuccess: (r) => {
      setResult(r);
      setCreated({});
      void queryClient.invalidateQueries({ queryKey: aiKey(workspaceId) });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const create = useMutation({
    mutationFn: (s: AiRuleSuggestionsDto["suggestions"][number]) => {
      const { rule: _rule, summary: _summary, ...suggestion } = s;
      return aiApi.createSuggestedRule(workspaceId, { clientId: result?.clientId ?? "", suggestion });
    },
    onSuccess: (rule, s) => {
      setCreated((prev) => ({ ...prev, [s.name]: rule.id }));
      void queryClient.invalidateQueries({ queryKey: rulesKey(workspaceId) });
      toast.success(`Regra "${rule.name}" criada desativada. Simule e ative em Regras.`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!clients.isPending && (clients.data ?? []).length === 0) {
    return <EmptyState icon={<Shield className="size-8" aria-hidden />} title="Nenhum cliente" description="Cadastre um cliente, vincule as contas e defina as metas (Clientes → ⋯ → Metas) para receber sugestões de regras." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} />
        <Button onClick={() => clientId && suggest.mutate(clientId)} disabled={!clientId} loading={suggest.isPending}>
          <Sparkles /> Sugerir regras
        </Button>
        <p className="text-xs text-muted-foreground sm:ml-2">As regras nascem desativadas: você revisa, simula e ativa na tela de Regras.</p>
      </div>
      {result ? (
        <>
          {result.warnings.map((w) => (
            <Alert key={w}>
              <AlertDescription>{w}</AlertDescription>
            </Alert>
          ))}
          <div className="grid gap-3 md:grid-cols-2">
            {result.suggestions.map((s) => (
              <Card key={s.name}>
                <CardHeader>
                  <CardTitle className="text-base">{s.name}</CardTitle>
                  <CardDescription>{s.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs">
                    <Badge variant="outline" className="mr-2">
                      {RULE_LEVEL_LABELS[s.level]}
                    </Badge>
                    {s.summary}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    A cada {s.frequencyMinutes} min · contas: {result.adAccountMetaIds.join(", ")}
                    {s.maxBudget !== null ? ` · teto ${s.maxBudget}` : ""}
                    {s.minBudget !== null ? ` · piso ${s.minBudget}` : ""}
                  </p>
                  {created[s.name] ? (
                    <Button asChild variant="outline" size="sm">
                      <Link to="/app/$workspaceId/regras/$ruleId" params={{ workspaceId, ruleId: created[s.name] ?? "" }}>
                        Abrir regra (desativada)
                      </Link>
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => create.mutate(s)} loading={create.isPending && create.variables?.name === s.name}>
                      <Shield /> Criar regra (desativada)
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

// ---- Uso do mês -----------------------------------------------------------------------------------------------

function UsagePanel({ workspaceId }: { workspaceId: string }) {
  const usage = useQuery(aiQueries.usage(workspaceId));
  if (usage.isPending) return <LoadingState rows={4} />;
  if (usage.isError) return <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />;
  const u = usage.data;
  const pct = u.quota.monthlyCredits > 0 ? Math.min(100, Math.round((u.quota.usedCredits / u.quota.monthlyCredits) * 100)) : 0;
  const chart = u.byResource.map((r) => ({ name: r.label, creditos: r.credits, chamadas: r.calls }));
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Créditos de IA em {u.month}</CardTitle>
          <CardDescription>
            {u.quota.usedCredits} de {u.quota.monthlyCredits} créditos usados ({pct}%) · {u.totalCalls} chamada(s) · custo estimado {formatUsdMicros(u.totalCostUsdMicros)} · zera em {formatDate(u.quota.resetsAt)}. 1 crédito = 1.000 tokens processados.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className={`h-full ${pct >= 90 ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Por recurso</CardTitle>
        </CardHeader>
        <CardContent>
          {u.totalCalls === 0 ? (
            <EmptyState icon={<MessageSquare className="size-8" aria-hidden />} title="Nenhum uso neste mês" description="Pergunte à IA em Relatórios ou Campanhas, gere variações no assistente de criação ou sugira uma estrutura." />
          ) : (
            <>
              <div className="h-56">
                <ResponsiveContainer>
                  <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.15} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => [String(value ?? 0), "Créditos"]} />
                    <Bar dataKey="creditos" name="Créditos" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recurso</TableHead>
                    <TableHead className="text-right">Chamadas</TableHead>
                    <TableHead className="text-right">Créditos</TableHead>
                    <TableHead className="text-right">Tokens (entrada / saída)</TableHead>
                    <TableHead className="text-right">Custo est.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {u.byResource.map((r) => (
                    <TableRow key={r.resource}>
                      <TableCell>{AI_RESOURCE_LABELS[r.resource]}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.calls}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.credits}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.inputTokens.toLocaleString("pt-BR")} / {r.outputTokens.toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatUsdMicros(r.costUsdMicros)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
      {u.recent.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Últimas chamadas</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Recurso</TableHead>
                  <TableHead>Quem</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead className="text-right">Créditos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {u.recent.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs whitespace-nowrap">{formatRelative(r.createdAt)}</TableCell>
                    <TableCell>{AI_RESOURCE_LABELS[r.resource]}</TableCell>
                    <TableCell>{r.userName ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.model}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.credits}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
